import React from "react";
import { Heart, ArrowRight, Check, Quote } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { PageHero, GoldBtn, OutlineBtn } from "@/components/Bits";
import { IMAGES, TIMELINE, MEMBERSHIPS, WHO_WE_ARE, ETHIOPIA_ABOUT, TESTIMONIALS } from "@/config/content";
import { useDonate } from "@/context/DonateContext";

export default function Ethiopia() {
  const { openDonate } = useDonate();
  return (
    <div>
      <Seo title="Our Work in Ethiopia" description="How EHSIMFFA and the Emperor Haile Selassie I Memorial Foundation support university scholarships for economically disadvantaged students across Ethiopia." path="/ethiopia" />
      <PageHero
        kicker="EHSIMF in Ethiopia"
        title="Two organizations, one shared commitment."
        subtitle="EHSIMFFA works hand in hand with the Emperor Haile Selassie Memorial Foundation (EHSMF) in Ethiopia to fund scholarships for university students with demonstrated financial need."
        image={IMAGES.members}
      />

      {/* What EHSIMF in Ethiopia does */}
      <section className="mx-auto max-w-5xl px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <div className="kicker mb-4">The Foundation in Ethiopia</div>
          <h2 className="font-serif text-3xl sm:text-4xl font-light leading-tight text-ivory">{ETHIOPIA_ABOUT.heading}</h2>
          {ETHIOPIA_ABOUT.paragraphs.map((p, i) => (
            <p key={i} className="mt-5 text-lg leading-relaxed text-ivory2">{p}</p>
          ))}
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-20 sm:pb-28 grid gap-14 lg:grid-cols-2 lg:items-center">
        <Reveal className="relative order-2 lg:order-1">
          <div className="absolute -inset-3 border border-gold/20" aria-hidden="true" />
          <img src={IMAGES.members} alt="Members of the Emperor Haile Selassie I Memorial Association" className="relative w-full object-cover" loading="lazy" />
        </Reveal>
        <Reveal delay={0.1} className="order-1 lg:order-2">
          <h2 className="font-serif text-4xl sm:text-5xl font-light text-ivory">The connection between the U.S. and Ethiopia.</h2>
          <p className="mt-6 text-lg leading-relaxed text-ivory2">{WHO_WE_ARE.mission}</p>
          <p className="mt-4 text-lg leading-relaxed text-ivory2/85">
            In 2021, the organizations in the U.S. and Ethiopia reached an agreement on the roles and
            responsibilities of each in administering the scholarship program — ensuring equitable
            opportunities for promising students, including women and students with disabilities.
          </p>
        </Reveal>
      </section>

      {/* Timeline */}
      <section className="border-y border-gold/15 bg-ink2">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-20 sm:py-28">
          <Reveal><div className="kicker mb-4">Year by Year</div>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-ivory">A growing circle of scholars.</h2></Reveal>
          <div className="mt-14 space-y-10">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.06}>
                <div className="grid gap-4 sm:grid-cols-[160px_1fr] border-t border-gold/20 pt-8">
                  <div className="font-serif text-3xl gold-text">{t.year}</div>
                  <div>
                    <h3 className="font-serif text-2xl text-ivory">{t.title}</h3>
                    <p className="mt-2 text-ivory2/85 leading-relaxed">{t.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <Reveal className="max-w-3xl">
          <div className="kicker mb-4">In Their Words</div>
          <h2 className="font-serif text-4xl sm:text-5xl font-light text-ivory">Hear from some of our students.</h2>
          <p className="mt-5 text-lg leading-relaxed text-ivory2">Discover the students whose lives were changed by EHSIMF scholarships.</p>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={(i % 2) * 0.08} className="flex flex-col border border-gold/20 bg-ink2 p-7">
              <Quote className="h-8 w-8 text-gold/70" aria-hidden="true" />
              <p className="mt-4 flex-1 text-ivory2/85 leading-relaxed">{t.text}</p>
              <p className="mt-5 border-t border-gold/15 pt-4 font-serif text-xl text-ivory">{t.name}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Membership levels */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <Reveal className="max-w-3xl">
          <div className="kicker mb-4">Ways to Give</div>
          <h2 className="font-serif text-4xl sm:text-5xl font-light text-ivory">Become a member. Sustain a scholar.</h2>
          <p className="mt-5 text-lg leading-relaxed text-ivory2">
            By becoming a member and making a monthly contribution, you help students afford basic necessities —
            food, clothing, hygiene supplies, transport — so they can complete their studies.
          </p>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {MEMBERSHIPS.map((m, i) => (
            <Reveal key={m.amount} delay={i * 0.1} className="group flex flex-col border border-gold/25 bg-ink p-8 transition-all hover:-translate-y-1 hover:border-gold/60">
              <div className="kicker text-gold/80">{m.title}</div>
              <div className="mt-4 font-serif text-6xl gold-text">${m.amount}<span className="text-2xl text-ivory2/60">/mo</span></div>
              <p className="mt-4 flex items-start gap-2 text-ivory2"><Check className="mt-1 h-5 w-5 shrink-0 text-gold" /> Supports {m.students} during the academic year.</p>
              <p className="mt-2 text-sm text-ivory2/60">Equivalent to {m.note}.</p>
              <button
                onClick={() => openDonate(m.amount === 40 ? null : m.amount)}
                data-testid={`membership-${m.amount}`}
                className="mt-6 rounded-sm border border-gold/50 py-3 text-sm font-bold uppercase tracking-wider text-gold transition-colors hover:bg-gold hover:text-ink"
              >
                Give ${m.amount} Monthly
              </button>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 text-center">
          <GoldBtn onClick={() => openDonate()} testid="ethiopia-donate"><Heart className="h-5 w-5" /> Make a One-Time Gift</GoldBtn>
        </div>
      </section>
    </div>
  );
}
